/**
 * KI-Autofunktion der Gefaehrdungsbeurteilung (Plan §6, Bauvertrag §14.4 D).
 *
 * Zwei Proxy-Aktionen (supabase/functions/gemini-proxy/index.ts):
 *   - `gbuVorschlag` (K1): Gefaehrdungen mit Faktor, W/S vorher und nachher,
 *     Massnahmen S/T/O/P, Vorsorgeanlass und Vorschriften fuer EINEN
 *     Arbeitsbereich, dazu ein Vorschlag fuer den Kopf.
 *   - `gbuPruefen` (K2): Vollstaendigkeit einer GBU gegen die 11 Faktoren und
 *     das Kataster, je Hinweis optional ein fertiger Vorschlag.
 *
 * Muster `assistKoordinierung`: der Kontext wird HIER rein gebaut, der Prompt
 * steht im Proxy, die Antwort wird hier gelesen und geprueft. Uebernommen wird
 * nichts automatisch — der Pruefbildschirm hakt an, `gbuService.
 * uebernehmeKiAuswahl` fuegt nur ein und ueberschreibt nie.
 *
 * Rein bis auf die zwei Aufrufe ueber geminiService.ts: kein DOM, kein
 * Supabase, kein React. Die Tests laufen ohne jsdom.
 *
 * ═══ WAS AN DIE KI GEHT ═══════════════════════════════════════════════════
 * Feld fuer Feld abgeschrieben, nie ein Datensatz weitergereicht:
 *   • Arbeitsbereich: Bezeichnung, Bereich, Beschreibung der Taetigkeit.
 *   • Arbeitsmittel: Bezeichnung und Typ.
 *   • Stoffe: Art, Bezeichnung, Signalwort, H-Saetze, GHS-Piktogramme,
 *     Risikogruppe (Biostoffe), ob die Substitution geprueft ist.
 *   • Branche, bereits erfasste Gefaehrdungen (Faktor + Text).
 *   • Fuer die Pruefung zusaetzlich: Massnahmen (Art, Text, PSA-Code),
 *     Vorsorge-Codes, Vorschriften, PSA-Grundausstattung, organisatorische
 *     Massnahmen und nur ALS JA/NEIN, welche Notfallfelder gefuellt sind.
 * Jeder Freitext laeuft durch `ohnePersonendaten` und wird gekappt.
 *
 * ═══ WAS BEWUSST NICHT MITGEHT ════════════════════════════════════════════
 *   • Mitarbeiter und ihre Zuordnung zu Bereichen, `beteiligte`, `rollen`,
 *     `verantwortlich`, Termine, Umsetzungs- und Wirksamkeitsvermerke.
 *   • `ort` des Bereichs (kann eine Anschrift sein), Hersteller, Pruefer,
 *     Notizen, Sicherheitsdatenblaetter, CAS-Nummern.
 *   • Notfall-Wortlaut (Ersthelfer, Rufnummern) und alles zum Kunden.
 *   • Die Datenbank-Ids: Arbeitsmittel und Stoffe gehen mit Kurzzeichen
 *     („am1", „st2") hinaus. Die Zuordnung zurueck kennt nur der Client
 *     (`kontext.ids`); ein Kurzzeichen, das nicht im Kontext stand, wird
 *     verworfen. Ein Sprachmodell schreibt eine UUID gelegentlich falsch ab —
 *     ein Kurzzeichen nicht.
 *
 * ═══ AUSFALL ══════════════════════════════════════════════════════════════
 * Ein KI-Ausfall darf NIE wie „keine Gefaehrdung" aussehen. Deshalb:
 *   • `fordereGbuVorschlagAn` liefert bei Netz- oder Antwortfehler
 *     `status:'fehler'` MIT einem Ersatz aus Kataster und Faktorenkatalog
 *     (`quelle:'ersatz'`, im Pruefbildschirm nichts vorangehakt).
 *   • Ein Antwortkoerper ohne die erwarteten Felder (`{}`) oder mit lauter
 *     unbrauchbaren Eintraegen ist ein Fehlversuch, kein leeres Ergebnis.
 *   • Eine abgeschnittene Antwort (MAX_TOKENS) traegt `abgeschnitten: true`.
 *
 * ═══ VORSCHRIFTEN ═════════════════════════════════════════════════════════
 * Beide Aktionen sind ERZEUGEND und laufen durch die Regelwerkspruefung des
 * Proxys (_shared/regelwerk.ts). Vorschriften stehen nur in `vorschriften[]`;
 * der ArbMedVV-Anlass nur als `anlassCode` (kein Regelwerksfeld — dort
 * schnitte `bereinigeAngabe` „Anhang", „Teil" und „Abs." ab). Jede Zeile
 * traegt `gefaehrdung`, damit der Dialog KiRegelwerkHinweis sagt, aus welcher
 * Zeile eine verworfene Vorschrift stammt.
 */

import type {
  GbuKiErgebnis,
  GbuKiKopfVorschlag,
  GbuKiPruefErgebnis,
  GbuKiPruefHinweis,
  GbuKiVorschlag,
  GbuKiVorschlagGefaehrdung,
  GbuKiVorschlagMassnahme,
  GbuKiVorschlagVorsorge,
  GbuNotfall,
  GbuVoll,
  GefaehrdungsFaktor,
  Kataster,
  MassnahmeArt,
  MassnahmeVerweis,
  Signalwort,
  SifaArbeitsbereich,
  SifaArbeitsmittel,
  SifaGbuGefaehrdung,
  SifaStoff,
  StoffArt,
} from './gbuTypes';
import { ohnePersonendaten } from '../aiAssistContext';
import { isTruncatedAiResponse, rawAiText, salvageJsonArray } from '../aiJsonSalvage';
import { GEFAEHRDUNGSFAKTOREN } from './kataloge/gefaehrdungsfaktoren';
import { PSA_KATALOG, istPsaCode } from './kataloge/psaKatalog';
import { ARBMEDVV_ANLAESSE, istAnlassCode } from './kataloge/arbmedvvAnlaesse';
import { gbuPruefen, gbuVorschlag } from '../geminiService';

// ═══════════════════════════════════════════════════════════════════════════
// 0) OBERGRENZEN
// ═══════════════════════════════════════════════════════════════════════════
//
// Gekappt wird HIER, an der Stelle, die die Daten kennt — der Proxy kappt
// den fertigen JSON-Text nur noch als Notnagel (40.000 Zeichen). Die Zahlen
// halten den groessten Kontext deutlich darunter (Test).

/** Gefaehrdungen je Vorschlag; der Prompt verlangt dieselbe Grenze (in der Regel 8–15, hoechstens 20). */
export const GBU_KI_MAX_GEFAEHRDUNGEN = 20;
/** Massnahmen je Gefaehrdung (Prompt: 5, eine Reserve). */
export const GBU_KI_MAX_MASSNAHMEN = 6;
/** Vorsorgeanlaesse je Gefaehrdung. */
export const GBU_KI_MAX_VORSORGE = 4;
/** Vorschriften je Gefaehrdung (Prompt: 3) bzw. im Kopf (Prompt: 6). */
export const GBU_KI_MAX_VORSCHRIFTEN = 4;
export const GBU_KI_MAX_KOPF_VORSCHRIFTEN = 8;
/** Kataster-Eintraege und bereits erfasste Gefaehrdungen im Kontext. */
export const GBU_KI_MAX_ARBEITSMITTEL = 30;
export const GBU_KI_MAX_STOFFE = 30;
export const GBU_KI_MAX_VORHANDENE = 40;
/** Gefaehrdungen einer GBU, die in die Pruefung gehen. */
export const GBU_KI_MAX_PRUEF_GEFAEHRDUNGEN = 60;
/** Hinweise je Pruefung. */
export const GBU_KI_MAX_HINWEISE = 15;
/** Ersatz-Vorschlaege ohne KI — mehr ist keine Pruefliste mehr. */
export const GBU_KI_MAX_ERSATZ = 30;

// ═══════════════════════════════════════════════════════════════════════════
// 1) KLEINE REINE HELFER
// ═══════════════════════════════════════════════════════════════════════════

const istObjekt = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

const eigen = (o: object, k: string): boolean => Object.prototype.hasOwnProperty.call(o, k);

/** Freitext auf Laenge gebracht, Leerraum zusammengezogen. */
const text = (wert: unknown, max = 400): string => {
  if (typeof wert !== 'string') return '';
  const sauber = wert.replace(/\s+/g, ' ').trim();
  return sauber.length > max ? `${sauber.slice(0, max - 1)}…` : sauber;
};

/** Freitext, der das Haus verlaesst: erst Personendaten heraus, dann kappen. */
const hinaus = (wert: unknown, max = 400): string =>
  text(typeof wert === 'string' ? ohnePersonendaten(wert) : '', max);

/**
 * Wie `hinaus`, aber je Listenglied (an Komma und Semikolon getrennt). Der
 * Filter liest „Rolle, Grosswort" als Rolle plus Name: aus „Vorarbeiter,
 * Fachkraft fuer Arbeitssicherheit" wuerde sonst „Vorarbeiter [Name
 * entfernt] fuer Arbeitssicherheit". Glieder, von denen nur der Platzhalter
 * bleibt, fallen bei `ohneLeere` weg.
 */
const PLATZHALTER_NUR = /^\[(?:Name|Telefonnummer|E-Mail) entfernt\]$/;
const hinausJeGlied = (wert: unknown, max = 400, ohneLeere = false): string => {
  if (typeof wert !== 'string') return '';
  const teile = wert.split(/(\s*[,;]\s*)/);
  if (!ohneLeere) return text(teile.map((t, i) => (i % 2 ? t : ohnePersonendaten(t))).join(''), max);
  const glieder = teile
    .filter((_, i) => i % 2 === 0)
    .map(t => text(ohnePersonendaten(t), max))
    .filter(t => t && !PLATZHALTER_NUR.test(t));
  return text(glieder.join(', '), max);
};

/** Liste von Freitexten, ohne Leere und Doppelte, gekappt. */
const texte = (wert: unknown, anzahl: number, max = 300): string[] => {
  const raus: string[] = [];
  for (const w of Array.isArray(wert) ? wert : []) {
    const t = text(w, max);
    if (t && !raus.includes(t)) raus.push(t);
    if (raus.length >= anzahl) break;
  }
  return raus;
};

/** Code-Vergleich unabhaengig von Gross/klein, Umlauten und Trennzeichen. */
const normCode = (wert: unknown): string =>
  typeof wert === 'string'
    ? wert.trim().toLowerCase()
      .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
      .replace(/[\s/-]+/g, '_')
    : '';

/**
 * W oder S auf 1–3 geklemmt. Fehlt der Wert oder ist er keine Zahl, gilt
 * `ersatz` — die mittlere Stufe, wie `prioritaet` in aiAssistContext.ts:
 * eine hohe Zahl wuerde Alarm schlagen, eine niedrige verharmlosen, beides
 * waere eine Aussage, die das Modell nicht getroffen hat.
 */
export const klemmeStufe = (wert: unknown, ersatz = 2): number => {
  const n = typeof wert === 'number'
    ? wert
    : typeof wert === 'string' && wert.trim() ? Number(wert.trim().replace(',', '.')) : NaN;
  if (!Number.isFinite(n)) return ersatz;
  return Math.min(3, Math.max(1, Math.round(n)));
};

// ═══════════════════════════════════════════════════════════════════════════
// 2) CODES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die 11 Faktor-Codes aus gbuTypes. Als Record, damit der Compiler meldet,
 * wenn ein Code im Typ dazukommt und hier fehlt.
 */
const FAKTOR_CODES: Readonly<Record<GefaehrdungsFaktor, true>> = {
  mechanisch: true,
  elektrisch: true,
  gefahrstoffe: true,
  biologisch: true,
  brand_explosion: true,
  thermisch: true,
  physikalisch: true,
  arbeitsumgebung: true,
  physische_belastung: true,
  psychisch: true,
  sonstige: true,
};

export const GBU_FAKTOR_CODES = Object.keys(FAKTOR_CODES) as GefaehrdungsFaktor[];

const istFaktorCode = (code: unknown): code is GefaehrdungsFaktor =>
  typeof code === 'string' && eigen(FAKTOR_CODES, code);

const leseFaktor = (wert: unknown): GefaehrdungsFaktor | null => {
  const c = normCode(wert);
  return istFaktorCode(c) ? c : null;
};

const faktorText = (code: GefaehrdungsFaktor): string =>
  GEFAEHRDUNGSFAKTOREN.find(f => f.code === code)?.label || code;

/** Codes aus den Katalogen von Paket B — exakt, sonst klein, sonst gross. */
const katalogCode = (wert: unknown, gilt: (c: string) => boolean): string | null => {
  if (typeof wert !== 'string') return null;
  const t = wert.trim();
  if (!t) return null;
  return [t, t.toLowerCase(), t.toUpperCase()].find(c => gilt(c)) ?? null;
};

export interface GbuKiCode {
  code: string;
  label: string;
  /** Faktoren: Beispiele aus dem Katalog, damit das Modell richtig einordnet. */
  hinweis?: string;
}

/** Erlaubte Codes — gehen als `payload.codes` mit und stehen im Prompt als Liste. */
export interface GbuKiCodes {
  faktoren: GbuKiCode[];
  psa: GbuKiCode[];
  anlaesse: GbuKiCode[];
}

/**
 * Die Codelisten fuer den Prompt. Nur Code und Bezeichnung: die Fundstelle
 * des Anlasses und die Norm der PSA bleiben hier, damit das Modell sie nicht
 * in einen Freitext abschreibt.
 */
export const gbuKiCodes = (): GbuKiCodes => ({
  faktoren: GBU_FAKTOR_CODES.map(code => {
    const eintrag = GEFAEHRDUNGSFAKTOREN.find(f => f.code === code);
    const label = text(eintrag?.label, 120) || code;
    // Beispiele ohne die Klammerzusaetze und nur so viele, wie in 240 Zeichen passen.
    let hinweis = '';
    for (const b of eintrag?.beispiele ?? []) {
      const kurz = text(typeof b === 'string' ? b.replace(/\s*\([^)]*\)/g, '') : '', 80);
      if (!kurz) continue;
      const neu = hinweis ? `${hinweis}; ${kurz}` : kurz;
      if (neu.length > 240) break;
      hinweis = neu;
    }
    return hinweis ? { code, label, hinweis } : { code, label };
  }),
  psa: PSA_KATALOG.map(p => ({ code: p.code, label: text(p.label, 120) })),
  anlaesse: ARBMEDVV_ANLAESSE.map(a => ({ code: a.code, label: text(a.label, 160) })),
});

// ═══════════════════════════════════════════════════════════════════════════
// 3) KONTEXT
// ═══════════════════════════════════════════════════════════════════════════

export interface GbuKiBereich {
  bezeichnung: string;
  bereich: string;
  beschreibung: string;
}

export interface GbuKiArbeitsmittel {
  /** Kurzzeichen „am1", „am2" … */
  ref: string;
  bezeichnung: string;
  typ: string;
}

export interface GbuKiStoff {
  /** Kurzzeichen „st1", „st2" … */
  ref: string;
  art: StoffArt;
  bezeichnung: string;
  signalwort: Signalwort | null;
  hSaetze: string[];
  ghs: string[];
  risikogruppe: number | null;
  substitutionGeprueft: boolean;
}

/** Genau das geht als `payload.kontext` an `gbuVorschlag` — nichts sonst. */
export interface GbuKiAnfrage {
  branche: string;
  bereich: GbuKiBereich;
  arbeitsmittel: GbuKiArbeitsmittel[];
  /** Nicht uebermittelte Arbeitsmittel (Kappung) — kein Schluss auf Vollstaendigkeit. */
  weitereArbeitsmittel: number;
  stoffe: GbuKiStoff[];
  weitereStoffe: number;
  /** Bereits erfasst — die KI soll nichts davon wiederholen. */
  vorhandene: Array<{ faktor: GefaehrdungsFaktor; gefaehrdung: string }>;
  weitereVorhandene: number;
}

/**
 * Kontext eines Vorschlags. Nur `anfrage` und `codes` verlassen den Client;
 * der Rest bleibt hier und dient Leser, Ersatz und Dublettenabgleich.
 */
export interface GbuKiKontext {
  anfrage: GbuKiAnfrage;
  codes: GbuKiCodes;
  /** Kurzzeichen → Datenbank-Id (Arbeitsmittel „am…", Stoffe „st…"). */
  ids: Readonly<Record<string, string>>;
  /** Kurzzeichen der Arbeitsmittel, die elektrisch betrieben aussehen (nur Ersatz). */
  elektrisch: readonly string[];
  /** Voller Bestand fuer `ohneDubletten` — ungekappt, nie gesendet. */
  vorhandene: ReadonlyArray<Pick<SifaGbuGefaehrdung, 'faktor' | 'gefaehrdung'>>;
}

export type ArbeitsmittelEingabe = Pick<SifaArbeitsmittel, 'id' | 'bezeichnung' | 'typ'>
  & Partial<Pick<SifaArbeitsmittel, 'pruefung' | 'aktiv'>>;

export type StoffEingabe = Pick<SifaStoff, 'id' | 'art' | 'bezeichnung' | 'signalwort' | 'hSaetze' | 'ghs' | 'risikogruppe'>
  & Partial<Pick<SifaStoff, 'substitutionGeprueftAm' | 'aktiv'>>;

export interface GbuKiEingabe {
  bereich: Pick<SifaArbeitsbereich, 'bezeichnung' | 'bereich' | 'beschreibung'> | null | undefined;
  arbeitsmittel?: ReadonlyArray<ArbeitsmittelEingabe> | null;
  stoffe?: ReadonlyArray<StoffEingabe> | null;
  branche?: string | null;
  vorhandene?: ReadonlyArray<Pick<SifaGbuGefaehrdung, 'faktor' | 'gefaehrdung'>> | null;
}

/**
 * Sieht das Arbeitsmittel elektrisch betrieben aus? Grob und nur fuer den
 * Ersatz: ein falscher Treffer kostet einen Vorschlag, den niemand anhakt —
 * ein fehlender den Hinweis auf den elektrischen Schlag.
 */
const ELEKTRISCH =
  /elektr|dguv[\s-]*v(?:orschrift)?[\s-]*3\b|\bvde\b|akku|kabel|strom|\b\d{2,3}\s?v\b|motor|maschine|s(?:ä|ae)ge|bohr|schleif|flex|fr(?:ä|ae)se|r(?:ü|ue)ttler|mischer|pumpe|kompressor|leuchte|lampe|strahler|verl(?:ä|ae)ngerung|verteiler|trenner|hobel|heizl(?:ü|ue)fter|bautrockner|schwei(?:ß|ss)/i;

export const istElektrisch = (am: Pick<ArbeitsmittelEingabe, 'bezeichnung' | 'typ'> & { pruefung?: string | null }): boolean =>
  ELEKTRISCH.test([am.bezeichnung, am.typ, am.pruefung].filter(t => typeof t === 'string').join(' '));

const H_SATZ = /^(?:EU)?H\d{3}[A-Za-z]{0,3}$/i;
const GHS = /^GHS0[1-9]$/i;

/** Aktive Eintraege mit Id und Bezeichnung, je Id einmal. */
const aktive = <T extends { id: string; bezeichnung: string; aktiv?: boolean }>(liste: ReadonlyArray<T> | null | undefined): T[] => {
  const gesehen = new Set<string>();
  return (liste ?? []).filter(e => {
    if (!e || e.aktiv === false || !e.id || !text(e.bezeichnung) || gesehen.has(e.id)) return false;
    gesehen.add(e.id);
    return true;
  });
};

const alsKiArbeitsmittel = (a: ArbeitsmittelEingabe, ref: string): GbuKiArbeitsmittel => ({
  ref,
  bezeichnung: hinaus(a.bezeichnung, 120),
  typ: hinaus(a.typ, 80),
});

const alsKiStoff = (s: StoffEingabe, ref: string): GbuKiStoff => ({
  ref,
  art: s.art === 'biostoff' ? 'biostoff' : 'gefahrstoff',
  bezeichnung: hinaus(s.bezeichnung, 120),
  signalwort: s.signalwort === 'gefahr' || s.signalwort === 'achtung' ? s.signalwort : null,
  hSaetze: texte(s.hSaetze, 20, 12).filter(h => H_SATZ.test(h)).map(h => h.toUpperCase()),
  ghs: texte(s.ghs, 9, 6).filter(g => GHS.test(g)).map(g => g.toUpperCase()),
  risikogruppe: typeof s.risikogruppe === 'number' && s.risikogruppe >= 1 && s.risikogruppe <= 4
    ? Math.round(s.risikogruppe) : null,
  substitutionGeprueft: !!s.substitutionGeprueftAm,
});

const alsKiBereich = (b: GbuKiEingabe['bereich']): GbuKiBereich => ({
  bezeichnung: hinaus(b?.bezeichnung, 120),
  bereich: hinaus(b?.bereich, 120),
  beschreibung: hinaus(b?.beschreibung, 1200),
});

const alsVorhandene = (liste: GbuKiEingabe['vorhandene']) =>
  (liste ?? [])
    .filter(v => v && !!text(v.gefaehrdung))
    .map(v => ({ faktor: istFaktorCode(v.faktor) ? v.faktor : 'sonstige' as GefaehrdungsFaktor, gefaehrdung: hinaus(v.gefaehrdung, 200) }));

/**
 * Kontext fuer `gbuVorschlag` (K1). Ohne Personendaten, mit Kurzzeichen statt
 * Ids. `vorhandene` sind die schon erfassten Gefaehrdungen der GBU — sie gehen
 * gekappt an die KI (nicht wiederholen) und voll in den Dublettenabgleich.
 */
export const baueGbuKiKontext = (eingabe: GbuKiEingabe): GbuKiKontext => {
  const ids: Record<string, string> = {};
  const elektrisch: string[] = [];

  const alleMittel = aktive(eingabe.arbeitsmittel);
  const mittel = alleMittel.slice(0, GBU_KI_MAX_ARBEITSMITTEL).map((a, i) => {
    const ref = `am${i + 1}`;
    ids[ref] = a.id;
    if (istElektrisch(a)) elektrisch.push(ref);
    return alsKiArbeitsmittel(a, ref);
  });

  const alleStoffe = aktive(eingabe.stoffe);
  const stoffe = alleStoffe.slice(0, GBU_KI_MAX_STOFFE).map((s, i) => {
    const ref = `st${i + 1}`;
    ids[ref] = s.id;
    return alsKiStoff(s, ref);
  });

  const vorhandene = alsVorhandene(eingabe.vorhandene);

  return {
    anfrage: {
      branche: hinaus(eingabe.branche, 120),
      bereich: alsKiBereich(eingabe.bereich),
      arbeitsmittel: mittel,
      weitereArbeitsmittel: alleMittel.length - mittel.length,
      stoffe,
      weitereStoffe: alleStoffe.length - stoffe.length,
      vorhandene: vorhandene.slice(0, GBU_KI_MAX_VORHANDENE),
      weitereVorhandene: Math.max(0, vorhandene.length - GBU_KI_MAX_VORHANDENE),
    },
    codes: gbuKiCodes(),
    ids,
    elektrisch,
    vorhandene: (eingabe.vorhandene ?? []).filter(v => v && !!text(v.gefaehrdung)),
  };
};

/** Was an `gbuPruefen` geht. */
export interface GbuKiPruefAnfrage {
  branche: string;
  bereich: GbuKiBereich;
  titel: string;
  kopf: {
    vorschriften: string[];
    psaGrundausstattung: string;
    orgMassnahmen: string[];
    /** Nur WELCHE Notfallfelder gefuellt sind — ihr Wortlaut traegt Namen und Rufnummern. */
    notfallErfasst: Array<keyof GbuNotfall>;
  };
  gefaehrdungen: Array<{
    nr: number;
    faktor: GefaehrdungsFaktor;
    gefaehrdung: string;
    folgen: string;
    wVor: number; sVor: number; wNach: number; sNach: number;
    arbeitsmittel: string[];
    stoffe: string[];
    vorschriften: string[];
    massnahmen: Array<{ art: MassnahmeArt; text: string; psaCode: string | null; verweis: MassnahmeVerweis | null }>;
    vorsorge: string[];
  }>;
  weitereGefaehrdungen: number;
  /** Gerechnet, nicht geschaetzt: Faktor-Gruppen ohne eine einzige Gefaehrdung. */
  faktorenOhneGefaehrdung: GefaehrdungsFaktor[];
  arbeitsmittel: Array<GbuKiArbeitsmittel & { inGbu: boolean }>;
  weitereArbeitsmittel: number;
  stoffe: Array<GbuKiStoff & { inGbu: boolean }>;
  weitereStoffe: number;
}

export interface GbuKiPruefKontext {
  anfrage: GbuKiPruefAnfrage;
  codes: GbuKiCodes;
  ids: Readonly<Record<string, string>>;
}

const NOTFALL_FELDER: ReadonlyArray<keyof GbuNotfall> = ['ersteHilfe', 'brand', 'notruf', 'zugaenge', 'meldung', 'kontakte'];

/**
 * Kontext fuer `gbuPruefen` (K2). Arbeitsmittel und Stoffe, auf die die GBU
 * verweist, stehen vorn und gehen immer mit (auch inaktive); dann die
 * uebrigen aktiven des Katasters.
 */
export const baueGbuPruefKontext = (voll: GbuVoll, kataster: Kataster, branche?: string | null): GbuKiPruefKontext => {
  const ids: Record<string, string> = {};
  const refVon = new Map<string, string>();
  const gefaehrdungen = [...voll.gefaehrdungen].sort((a, b) => a.sortOrder - b.sortOrder);

  const verwiesenAm = new Set(gefaehrdungen.flatMap(g => g.arbeitsmittelIds ?? []));
  const verwiesenSt = new Set(gefaehrdungen.flatMap(g => g.stoffIds ?? []));

  const mittelListe = [
    ...kataster.arbeitsmittel.filter(a => verwiesenAm.has(a.id)),
    ...aktive(kataster.arbeitsmittel).filter(a => !verwiesenAm.has(a.id)),
  ].filter(a => !!text(a.bezeichnung));
  const mittel = mittelListe.slice(0, GBU_KI_MAX_ARBEITSMITTEL).map((a, i) => {
    const ref = `am${i + 1}`;
    ids[ref] = a.id;
    refVon.set(a.id, ref);
    return { ...alsKiArbeitsmittel(a, ref), inGbu: verwiesenAm.has(a.id) };
  });

  const stoffListe = [
    ...kataster.stoffe.filter(s => verwiesenSt.has(s.id)),
    ...aktive(kataster.stoffe).filter(s => !verwiesenSt.has(s.id)),
  ].filter(s => !!text(s.bezeichnung));
  const stoffe = stoffListe.slice(0, GBU_KI_MAX_STOFFE).map((s, i) => {
    const ref = `st${i + 1}`;
    ids[ref] = s.id;
    refVon.set(s.id, ref);
    return { ...alsKiStoff(s, ref), inGbu: verwiesenSt.has(s.id) };
  });

  const refs = (liste: string[] | null | undefined) =>
    (liste ?? []).map(id => refVon.get(id)).filter((r): r is string => !!r);

  const gewaehlt = gefaehrdungen.slice(0, GBU_KI_MAX_PRUEF_GEFAEHRDUNGEN);
  const zeilen = gewaehlt.map((g, i) => ({
    nr: i + 1,
    faktor: istFaktorCode(g.faktor) ? g.faktor : 'sonstige' as GefaehrdungsFaktor,
    gefaehrdung: hinaus(g.gefaehrdung, 300),
    folgen: hinaus(g.folgen, 200),
    wVor: klemmeStufe(g.wVor), sVor: klemmeStufe(g.sVor),
    wNach: klemmeStufe(g.wNach), sNach: klemmeStufe(g.sNach),
    arbeitsmittel: refs(g.arbeitsmittelIds),
    stoffe: refs(g.stoffIds),
    vorschriften: texte(g.vorschriften, 8, 120),
    // Ohne `verantwortlich`, Termin und Umsetzungsvermerke: dort stehen Namen.
    massnahmen: voll.massnahmen
      .filter(m => m.gefaehrdungId === g.id)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .slice(0, 10)
      .map(m => ({ art: m.art, text: hinaus(m.text, 300), psaCode: m.psaCode ?? null, verweis: m.verweis ?? null })),
    vorsorge: voll.vorsorge.filter(v => v.gefaehrdungId === g.id).map(v => v.anlassCode).slice(0, 6),
  }));

  const genutzt = new Set(gefaehrdungen.map(g => g.faktor));
  const bereich = kataster.bereiche.find(b => b.id === voll.gbu.bereichId) ?? null;
  const notfall = voll.gbu.notfall ?? ({} as Partial<GbuNotfall>);

  return {
    anfrage: {
      branche: hinaus(branche, 120),
      bereich: alsKiBereich(bereich),
      titel: hinaus(voll.gbu.titel, 200),
      kopf: {
        vorschriften: texte(voll.gbu.vorschriften, 12, 120),
        psaGrundausstattung: hinaus(voll.gbu.psaGrundausstattung, 400),
        orgMassnahmen: (voll.gbu.orgMassnahmen ?? []).map(t => hinaus(t, 200)).filter(Boolean).slice(0, 12),
        notfallErfasst: NOTFALL_FELDER.filter(f => !!text(notfall[f])),
      },
      gefaehrdungen: zeilen,
      weitereGefaehrdungen: gefaehrdungen.length - gewaehlt.length,
      faktorenOhneGefaehrdung: GBU_FAKTOR_CODES.filter(c => !genutzt.has(c)),
      arbeitsmittel: mittel,
      weitereArbeitsmittel: mittelListe.length - mittel.length,
      stoffe,
      weitereStoffe: stoffListe.length - stoffe.length,
    },
    codes: gbuKiCodes(),
    ids,
  };
};

// ═══════════════════════════════════════════════════════════════════════════
// 4) ANTWORT LESEN
// ═══════════════════════════════════════════════════════════════════════════

/** Fuer die Oberflaeche, wenn die KI nichts Verwertbares lieferte. */
export const GBU_KI_KEINE_ANTWORT =
  'Die KI hat keine verwertbare Antwort geliefert. Das ist ein Fehlversuch und kein „keine Gefährdung". '
  + 'Bitte noch einmal versuchen; übernommen wird ohnehin nichts automatisch.';

/** Hinweis im Pruefergebnis, wenn die Antwort abgeschnitten war (der Typ hat dafuer kein Feld). */
export const GBU_KI_PRUEFUNG_ABGESCHNITTEN =
  'Die Antwort der KI war abgeschnitten — die Hinweisliste ist möglicherweise unvollständig. Bitte die Prüfung erneut starten.';

const VERWEISE: readonly MassnahmeVerweis[] = ['ba', 'unterweisung', 'pruefung', 'vorsorge'];
const STOP: readonly MassnahmeArt[] = ['S', 'T', 'O', 'P'];

/** Massnahmenart: der Buchstabe oder das ausgeschriebene Wort, sonst nichts. */
const leseArt = (wert: unknown): MassnahmeArt | null => {
  const t = normCode(wert);
  if (t === 's' || /^(?:substitution|ersatz)/.test(t)) return 'S';
  if (t === 't' || /^techni/.test(t)) return 'T';
  if (t === 'o' || /^organisat/.test(t)) return 'O';
  if (t === 'p' || /^(?:persoen|personen|psa)/.test(t)) return 'P';
  return null;
};

const leseVerweis = (wert: unknown): MassnahmeVerweis | null => {
  const t = normCode(wert);
  return (VERWEISE as readonly string[]).includes(t) ? t as MassnahmeVerweis : null;
};

/** Kurzzeichen → Ids, nur solche aus dem Kontext und mit passendem Praefix. */
const leseRefs = (wert: unknown, praefix: 'am' | 'st', ids: Readonly<Record<string, string>>): string[] => {
  const raus: string[] = [];
  for (const r of Array.isArray(wert) ? wert : []) {
    if (typeof r !== 'string') continue;
    const ref = r.trim().toLowerCase();
    if (!ref.startsWith(praefix) || !eigen(ids, ref)) continue;
    const id = ids[ref];
    if (id && !raus.includes(id)) raus.push(id);
  }
  return raus;
};

const leseMassnahmen = (wert: unknown, gid: string): GbuKiVorschlagMassnahme[] => {
  const raus: GbuKiVorschlagMassnahme[] = [];
  const gesehen = new Set<string>();
  for (const e of Array.isArray(wert) ? wert : []) {
    if (!istObjekt(e)) continue;
    const art = leseArt(e.art);
    const t = text(e.text ?? e.massnahme, 400);
    if (!art || !t) continue;
    const schluessel = vergleichsSchluessel(t);
    if (gesehen.has(schluessel)) continue;
    gesehen.add(schluessel);
    raus.push({
      tempId: '',
      art,
      text: t,
      // Nur P traegt PSA; ein unbekannter Code faellt weg, die Massnahme bleibt.
      psaCode: art === 'P' ? katalogCode(e.psaCode ?? e.psa, istPsaCode) : null,
      verweis: leseVerweis(e.verweis),
    });
    if (raus.length >= GBU_KI_MAX_MASSNAHMEN) break;
  }
  // STOP-Rangfolge, innerhalb einer Art in der Reihenfolge der KI.
  return raus
    .map((m, i) => ({ m, i }))
    .sort((a, b) => STOP.indexOf(a.m.art) - STOP.indexOf(b.m.art) || a.i - b.i)
    .map(({ m }, j) => ({ ...m, tempId: `${gid}-m${j + 1}` }));
};

const leseVorsorge = (wert: unknown, gid: string): GbuKiVorschlagVorsorge[] => {
  const raus: GbuKiVorschlagVorsorge[] = [];
  for (const e of Array.isArray(wert) ? wert : []) {
    const roh = istObjekt(e) ? e.anlassCode ?? e.anlass_code ?? e.code : e;
    const code = katalogCode(roh, istAnlassCode);
    if (!code || raus.some(v => v.anlassCode === code)) continue;
    raus.push({
      tempId: `${gid}-v${raus.length + 1}`,
      anlassCode: code,
      begruendung: istObjekt(e) ? text(e.begruendung, 300) || null : null,
    });
    if (raus.length >= GBU_KI_MAX_VORSORGE) break;
  }
  return raus;
};

/**
 * Eine Gefaehrdung aus der Antwort. Ohne gueltigen Faktor oder ohne Text wird
 * sie VERWORFEN — einen Faktor zu raten hiesse, sie in die falsche Gruppe der
 * Vollstaendigkeitspruefung zu legen.
 */
const leseGefaehrdung = (
  e: Record<string, unknown>,
  ids: Readonly<Record<string, string>>,
  gid: string,
): GbuKiVorschlagGefaehrdung | null => {
  const faktor = leseFaktor(e.faktor);
  const gefaehrdung = text(e.gefaehrdung, 300);
  if (!faktor || !gefaehrdung) return null;
  const wVor = klemmeStufe(e.wVor ?? e.w_vor);
  const sVor = klemmeStufe(e.sVor ?? e.s_vor);
  // Fehlt „nachher", gilt „vorher" (keine Senkung behauptet). Massnahmen
  // erhoehen weder W noch S: ein groesserer Nachher-Wert wird gekappt.
  const wNach = Math.min(klemmeStufe(e.wNach ?? e.w_nach, wVor), wVor);
  const sNach = Math.min(klemmeStufe(e.sNach ?? e.s_nach, sVor), sVor);
  return {
    tempId: gid,
    faktor,
    gefaehrdung,
    folgen: text(e.folgen, 300) || null,
    wVor, sVor, wNach, sNach,
    arbeitsmittelIds: leseRefs(e.arbeitsmittel ?? e.arbeitsmittelIds, 'am', ids),
    stoffIds: leseRefs(e.stoffe ?? e.stoffIds, 'st', ids),
    vorschriften: texte(e.vorschriften, GBU_KI_MAX_VORSCHRIFTEN, 120),
    massnahmen: leseMassnahmen(e.massnahmen, gid),
    vorsorge: leseVorsorge(e.vorsorge, gid),
    quelle: 'ki',
  };
};

const leseKopf = (wert: unknown): GbuKiKopfVorschlag => {
  const kopf: GbuKiKopfVorschlag = {};
  if (!istObjekt(wert)) return kopf;
  const vorschriften = texte(wert.vorschriften, GBU_KI_MAX_KOPF_VORSCHRIFTEN, 120);
  if (vorschriften.length) kopf.vorschriften = vorschriften;
  const org = texte(wert.orgMassnahmen, 10, 300);
  if (org.length) kopf.orgMassnahmen = org;
  for (const feld of ['unterlagen', 'psaGrundausstattung', 'randbedingungen', 'umwelt', 'koordination'] as const) {
    const t = text(wert[feld], 600);
    if (t) kopf[feld] = t;
  }
  // Beteiligte nur als Funktionen: was wie ein Name aussieht, faellt heraus.
  const beteiligte = hinausJeGlied(wert.beteiligte, 400, true);
  if (beteiligte) kopf.beteiligte = beteiligte;
  if (istObjekt(wert.notfall)) {
    // `kontakte` nie: Namen und Rufnummern kennt die KI nicht, und erfundene
    // Kontakte im Notfallabschnitt sind schlimmer als ein leeres Feld. Der
    // Rest geht vorsorglich durch den Personendaten-Filter.
    const notfall: Partial<GbuNotfall> = {};
    for (const feld of ['ersteHilfe', 'brand', 'notruf', 'zugaenge', 'meldung'] as const) {
      const t = hinausJeGlied(wert.notfall[feld], 400);
      if (t) notfall[feld] = t;
    }
    if (Object.keys(notfall).length) kopf.notfall = notfall;
  }
  return kopf;
};

/**
 * Rettet EINE benannte Liste aus unlesbarem Text — nur, wenn der Schluessel
 * im Text steht (salvageJsonArray faellt sonst auf das erste Objekt-Array
 * zurueck, Muster `rette` in aiAssistContext.ts).
 */
const rette = (rohText: string, schluessel: string): Record<string, unknown>[] => {
  if (!new RegExp(`["']${schluessel}["']\\s*:\\s*\\[`, 'i').test(rohText)) return [];
  return salvageJsonArray(rohText, [schluessel]).filter(istObjekt);
};

const alsObjekt = (rohText: string): Record<string, unknown> | null => {
  try {
    const v: unknown = JSON.parse(rohText.replace(/```(?:json)?/gi, '').trim());
    return istObjekt(v) ? v : null;
  } catch {
    return null;
  }
};

interface Koerper {
  k: Record<string, unknown>;
  abgeschnitten: boolean;
  /** Vom Proxy reparierter Text: der letzte Listeneintrag kann halb sein. */
  repariert: boolean;
}

/**
 * Der Antwortkoerper samt Abschneide-Marke. Eine unlesbare (meist
 * abgeschnittene) Antwort schickt der Proxy als `{ text }` — inzwischen
 * REPARIERT zu gueltigem JSON bis zum letzten vollstaendigen Element, sonst
 * bleibt die Rettung der benannten Liste. Beides heisst `abgeschnitten`.
 * `null`: nichts Lesbares.
 */
const koerperAus = (raw: unknown, liste: string): Koerper | null => {
  const rohText = rawAiText(raw);
  if (rohText) {
    const k = alsObjekt(rohText);
    if (k) return { k, abgeschnitten: true, repariert: true };
    const gerettet = rette(rohText, liste);
    return gerettet.length ? { k: { [liste]: gerettet }, abgeschnitten: true, repariert: false } : null;
  }
  // Geparst trotz MAX_TOKENS: das JSON war vollstaendig, nur die Marke zaehlt.
  return istObjekt(raw) ? { k: raw, abgeschnitten: isTruncatedAiResponse(raw), repariert: false } : null;
};

/**
 * Die Reparatur schliesst nur die Klammern — der LETZTE Eintrag der Liste
 * kann deshalb halb sein (Massnahmen ohne Vorsorge, Bewertung ohne
 * Nachher-Werte). Steht hinter der Liste noch ein weiteres Feld, war sie
 * vollstaendig; sonst faellt er weg. `salvageJsonArray` liefert dagegen nur
 * ganze Objekte.
 */
const ohneHalbenRest = (liste: unknown[], koerper: Koerper, danach: string[]): unknown[] =>
  koerper.repariert && liste.length > 0 && !danach.some(f => eigen(koerper.k, f)) ? liste.slice(0, -1) : liste;

/**
 * Liest die Antwort von `gbuVorschlag`.
 *
 * W/S auf 1–3 geklemmt, Faktor-, PSA- und Anlass-Codes gegen die Kataloge,
 * Kurzzeichen gegen den Kontext; Unbekanntes faellt weg. `null` heisst
 * Fehlversuch: kein Koerper, keines der erwarteten Felder, oder die KI hat
 * Eintraege geliefert und KEINER war brauchbar — das darf nie als „keine
 * Gefaehrdung" durchgehen.
 */
export const leseGbuVorschlag = (raw: unknown, kontext: Pick<GbuKiKontext, 'ids'>): GbuKiVorschlag | null => {
  const gelesen = koerperAus(raw, 'gefaehrdungen');
  if (!gelesen) return null;
  const { k, abgeschnitten } = gelesen;
  if (!['gefaehrdungen', 'kopf', 'hinweise'].some(f => eigen(k, f))) return null;

  const roh = ohneHalbenRest(Array.isArray(k.gefaehrdungen) ? k.gefaehrdungen : [], gelesen, ['kopf', 'hinweise']);
  const brauchbar = roh
    .map((e, i) => (istObjekt(e) ? leseGefaehrdung(e, kontext.ids, `ki-g${i + 1}`) : null))
    .filter((g): g is GbuKiVorschlagGefaehrdung => !!g);
  if (roh.length > 0 && brauchbar.length === 0) return null;
  const gefaehrdungen = brauchbar.slice(0, GBU_KI_MAX_GEFAEHRDUNGEN);

  const hinweise = texte(k.hinweise, 5, 300);
  const verworfen = roh.length - brauchbar.length;
  if (verworfen > 0) {
    hinweise.push(verworfen === 1
      ? 'Ein Vorschlag der KI war unbrauchbar (ohne Text oder mit unbekanntem Faktor) und wurde verworfen.'
      : `${verworfen} Vorschläge der KI waren unbrauchbar (ohne Text oder mit unbekanntem Faktor) und wurden verworfen.`);
  }

  return { gefaehrdungen, kopf: leseKopf(k.kopf), hinweise, abgeschnitten };
};

const PRUEF_ARTEN: readonly GbuKiPruefHinweis['art'][] = ['luecke', 'widerspruch', 'verbesserung'];

/**
 * Liest die Antwort von `gbuPruefen`. Ein Hinweis braucht einen Text; sein
 * Vorschlag wird wie im Vorschlag gelesen und faellt weg, wenn er
 * unbrauchbar ist — der Hinweis bleibt. `null` heisst Fehlversuch.
 */
export const leseGbuPruefung = (
  raw: unknown,
  kontext: Pick<GbuKiPruefKontext, 'ids'>,
): { hinweise: GbuKiPruefHinweis[]; abgeschnitten: boolean } | null => {
  const gelesen = koerperAus(raw, 'hinweise');
  if (!gelesen) return null;
  const { k, abgeschnitten } = gelesen;
  if (!eigen(k, 'hinweise')) return null;

  const roh = ohneHalbenRest(Array.isArray(k.hinweise) ? k.hinweise : [], gelesen, []);
  const hinweise: GbuKiPruefHinweis[] = [];
  roh.filter(istObjekt).forEach((e, i) => {
    const t = text(e.text, 500);
    if (!t || hinweise.length >= GBU_KI_MAX_HINWEISE) return;
    const art = normCode(e.art);
    const vorschlag = istObjekt(e.vorschlag) ? leseGefaehrdung(e.vorschlag, kontext.ids, `ki-p${i + 1}`) : null;
    hinweise.push({
      // Unbekannte Art wird „luecke": im Zweifel lauter statt leiser.
      art: (PRUEF_ARTEN as readonly string[]).includes(art) ? art as GbuKiPruefHinweis['art'] : 'luecke',
      text: t,
      faktor: leseFaktor(e.faktor) ?? vorschlag?.faktor ?? null,
      vorschlag,
    });
  });
  if (roh.length > 0 && hinweise.length === 0) return null;
  return { hinweise, abgeschnitten };
};

// ═══════════════════════════════════════════════════════════════════════════
// 5) DUBLETTEN
// ═══════════════════════════════════════════════════════════════════════════

const FUELLWOERTER = new Set([
  'der', 'die', 'das', 'den', 'dem', 'des', 'ein', 'eine', 'einer', 'eines', 'einem', 'einen',
  'und', 'oder', 'mit', 'von', 'vom', 'bei', 'beim', 'durch', 'an', 'am', 'im', 'in', 'zu', 'zum',
  'zur', 'auf', 'aus', 'fuer', 'gegen', 'ueber', 'unter', 'nach', 'vor', 'sowie', 'bzw', 'zb', 'ua',
]);

/**
 * Vergleichsschluessel eines Gefaehrdungstexts: klein, Umlaute aufgeloest,
 * ohne Satzzeichen und Fuellwoerter, Woerter sortiert. „Hautkontakt mit
 * Zement" und „Zement: Hautkontakt" ergeben denselben Schluessel.
 */
export const vergleichsSchluessel = (wert: string): string => {
  const woerter = (wert || '').toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter(w => w.length > 1 && !FUELLWOERTER.has(w));
  return [...new Set(woerter)].sort().join(' ');
};

/**
 * Blendet Vorschlaege aus, deren Text schon erfasst ist — und Doppelte
 * innerhalb des Vorschlags. Bestehende Zeilen werden nie veraendert; der
 * Abgleich ist reiner Text, der Faktor zaehlt nicht.
 */
export const ohneDubletten = (
  vorschlag: GbuKiVorschlag,
  vorhandene: ReadonlyArray<Pick<SifaGbuGefaehrdung, 'gefaehrdung'>> | null | undefined,
): GbuKiVorschlag => {
  const gesehen = new Set((vorhandene ?? []).map(v => vergleichsSchluessel(v?.gefaehrdung ?? '')).filter(Boolean));
  const gefaehrdungen = vorschlag.gefaehrdungen.filter(g => {
    const s = vergleichsSchluessel(g.gefaehrdung);
    if (!s || gesehen.has(s)) return false;
    gesehen.add(s);
    return true;
  });
  return { ...vorschlag, gefaehrdungen };
};

// ═══════════════════════════════════════════════════════════════════════════
// 6) ERSATZ OHNE KI
// ═══════════════════════════════════════════════════════════════════════════

/** Fuer die Oberflaeche ueber der Ersatzliste (Muster ufsaKatalogVorschlag.ts). */
export const GBU_ERSATZ_HINWEIS =
  'KI nicht erreichbar – diese Vorschläge stammen aus Kataster und Faktorenkatalog. Sie sind allgemeine '
  + 'Beispiele und keine Beurteilung dieser Tätigkeit. Nur übernehmen, was zutrifft, und W/S prüfen.';

/** Erster PSA-Code, dessen Bezeichnung zum ersten passenden Muster passt; sonst null. */
const psaCodeFuer = (...muster: RegExp[]): string | null => {
  for (const mu of muster) {
    const treffer = PSA_KATALOG.find(p => mu.test(p.label));
    if (treffer) return treffer.code;
  }
  return null;
};

type ErsatzMassnahme = Omit<GbuKiVorschlagMassnahme, 'tempId'>;

const mass = (art: MassnahmeArt, t: string, verweis: MassnahmeVerweis | null = null, psaCode: string | null = null): ErsatzMassnahme =>
  ({ art, text: t, psaCode: art === 'P' ? psaCode : null, verweis });

type ErsatzZeile = Omit<GbuKiVorschlagGefaehrdung, 'tempId' | 'massnahmen' | 'vorsorge' | 'quelle'> & {
  massnahmen: ErsatzMassnahme[];
};

/**
 * Generische Vorschlaege ohne KI: je Gefahrstoff Haut und Einatmen, je
 * Biostoff die Infektion, je elektrisch aussehendes Arbeitsmittel der
 * elektrische Schlag, einmal die mechanische Gefaehrdung an den
 * Arbeitsmitteln, immer physische und psychische Belastung. Die Vorschriften
 * stammen aus der festen Liste der Regelwerkspruefung. Kein Vorsorgeanlass:
 * dafuer fehlen Messwerte, und ein geratener Anlass waere eine Aussage.
 * `quelle: 'ersatz'` — der Pruefbildschirm hakt davon nichts vor.
 */
export const ersatzVorschlag = (kontext: GbuKiKontext): GbuKiVorschlag => {
  const chemieHandschuh = psaCodeFuer(/chemikal/i, /handschuh/i);
  const handschuh = psaCodeFuer(/handschuh.*mechan/i, /handschuh/i);
  const atemschutz = psaCodeFuer(/atem/i);
  const zeilen: ErsatzZeile[] = [];
  const id = (ref: string) => (eigen(kontext.ids, ref) ? [kontext.ids[ref]] : []);
  const bereich = kontext.anfrage.bereich.bezeichnung || 'dieser Tätigkeit';

  for (const s of kontext.anfrage.stoffe) {
    if (s.art === 'biostoff') {
      const schwere = (s.risikogruppe ?? 0) >= 3 ? 3 : 2;
      zeilen.push({
        faktor: 'biologisch',
        gefaehrdung: `Infektion durch ${s.bezeichnung}`,
        folgen: 'Infektionskrankheit, allergische oder toxische Wirkung',
        wVor: 2, sVor: schwere, wNach: 1, sNach: schwere,
        arbeitsmittelIds: [], stoffIds: id(s.ref), vorschriften: ['BioStoffV'],
        massnahmen: [
          mass('T', 'Kontakt und Aerosolbildung technisch vermeiden, Wasch- und Desinfektionsmöglichkeit bereitstellen'),
          mass('O', 'Hygieneplan aufstellen und anhand der Betriebsanweisung unterweisen', 'ba'),
          mass('P', 'Flüssigkeitsdichte Schutzhandschuhe tragen', null, chemieHandschuh),
        ],
      });
      continue;
    }
    zeilen.push({
      faktor: 'gefahrstoffe',
      gefaehrdung: `Hautkontakt mit ${s.bezeichnung}`,
      folgen: 'Hautreizung, Verätzung oder Sensibilisierung',
      wVor: 2, sVor: 2, wNach: 1, sNach: 2,
      arbeitsmittelIds: [], stoffIds: id(s.ref), vorschriften: ['GefStoffV'],
      massnahmen: [
        mass('S', 'Ersatz durch einen weniger gefährlichen Stoff oder ein hautschonendes Verfahren prüfen'),
        mass('O', 'Betriebsanweisung erstellen und anhand der Betriebsanweisung unterweisen', 'ba'),
        mass('O', 'Hautschutzplan aushängen'),
        mass('P', 'Geeignete Chemikalienschutzhandschuhe nach Sicherheitsdatenblatt tragen', null, chemieHandschuh),
      ],
    });
    zeilen.push({
      faktor: 'gefahrstoffe',
      gefaehrdung: `Einatmen von Dämpfen, Stäuben oder Aerosolen von ${s.bezeichnung}`,
      folgen: 'Reizung oder Schädigung der Atemwege',
      wVor: 2, sVor: 2, wNach: 1, sNach: 2,
      arbeitsmittelIds: [], stoffIds: id(s.ref), vorschriften: ['GefStoffV'],
      massnahmen: [
        mass('S', 'Staub- oder emissionsarme Lieferform bzw. Verfahren prüfen'),
        mass('T', 'Absaugung oder ausreichende Lüftung sicherstellen'),
        mass('P', 'Atemschutz nach Sicherheitsdatenblatt, wenn Absaugung und Lüftung nicht ausreichen', null, atemschutz),
      ],
    });
  }

  const elektrisch = new Set(kontext.elektrisch);
  for (const a of kontext.anfrage.arbeitsmittel) {
    if (!elektrisch.has(a.ref)) continue;
    zeilen.push({
      faktor: 'elektrisch',
      gefaehrdung: `Elektrischer Schlag durch beschädigtes Kabel, Stecker oder Gehäuse an ${a.bezeichnung}`,
      folgen: 'Körperdurchströmung, Verbrennungen, Sturz infolge Schreckreaktion',
      wVor: 2, sVor: 3, wNach: 1, sNach: 3,
      arbeitsmittelIds: id(a.ref), stoffIds: [], vorschriften: ['BetrSichV'],
      massnahmen: [
        mass('T', 'Betrieb über Fehlerstrom-Schutzeinrichtung bzw. geprüften Baustromverteiler'),
        mass('O', 'Wiederkehrende Prüfung durch eine befähigte Person festlegen', 'pruefung'),
        mass('O', 'Sichtprüfung vor jeder Benutzung; beschädigte Geräte sofort aussondern', 'unterweisung'),
      ],
    });
  }

  if (kontext.anfrage.arbeitsmittel.length > 0) {
    zeilen.push({
      faktor: 'mechanisch',
      gefaehrdung: 'Quetschen, Schneiden oder Erfassen durch bewegte Teile und Kanten der Arbeitsmittel',
      folgen: 'Schnitt-, Quetsch- oder Amputationsverletzungen',
      wVor: 2, sVor: 2, wNach: 1, sNach: 2,
      arbeitsmittelIds: kontext.anfrage.arbeitsmittel.flatMap(a => id(a.ref)),
      stoffIds: [], vorschriften: ['BetrSichV'],
      massnahmen: [
        mass('T', 'Schutzeinrichtungen vorhanden und wirksam; nicht entfernen oder umgehen'),
        mass('O', 'Bedienung nur durch unterwiesene Beschäftigte anhand der Betriebsanweisung', 'ba'),
        mass('P', 'Schutzhandschuhe tragen, wo kein Einzugsrisiko besteht', null, handschuh),
      ],
    });
  }

  zeilen.push({
    faktor: 'physische_belastung',
    gefaehrdung: `Physische Belastung durch Heben, Tragen oder Zwangshaltungen bei ${bereich}`,
    folgen: 'Muskel-Skelett-Erkrankungen, insbesondere des Rückens',
    wVor: 2, sVor: 2, wNach: 1, sNach: 2,
    arbeitsmittelIds: [], stoffIds: [], vorschriften: ['ArbSchG'],
    massnahmen: [
      mass('T', 'Hebe- und Transporthilfen bereitstellen'),
      mass('O', 'Lasten aufteilen, Tätigkeiten wechseln, zu zweit tragen'),
      mass('O', 'Unterweisung zum rückengerechten Heben und Tragen', 'unterweisung'),
    ],
  });
  zeilen.push({
    faktor: 'psychisch',
    gefaehrdung: `Psychische Belastung, z. B. durch Zeitdruck, Unterbrechungen oder Alleinarbeit bei ${bereich}`,
    folgen: 'Stress, Konzentrationsmangel und dadurch erhöhte Unfallgefahr',
    wVor: 2, sVor: 2, wNach: 1, sNach: 2,
    arbeitsmittelIds: [], stoffIds: [], vorschriften: ['ArbSchG'],
    massnahmen: [
      mass('O', 'Arbeitsabläufe, Pausen und Zuständigkeiten klar regeln; Belastung mit den Beschäftigten besprechen'),
    ],
  });

  const gekappt = zeilen.length > GBU_KI_MAX_ERSATZ;
  // Belastungen stehen hinten und duerfen bei der Kappung nicht wegfallen.
  const auswahl = gekappt ? [...zeilen.slice(0, GBU_KI_MAX_ERSATZ - 2), ...zeilen.slice(-2)] : zeilen;
  const gefaehrdungen: GbuKiVorschlagGefaehrdung[] = auswahl.map((z, i) => {
    const gid = `ersatz-g${i + 1}`;
    return {
      ...z,
      tempId: gid,
      massnahmen: z.massnahmen.map((mm, j) => ({ ...mm, tempId: `${gid}-m${j + 1}` })),
      vorsorge: [],
      quelle: 'ersatz',
    };
  });

  const abgedeckt = new Set<GefaehrdungsFaktor>([
    ...gefaehrdungen.map(g => g.faktor),
    ...kontext.vorhandene.map(v => v.faktor).filter(istFaktorCode),
  ]);
  const offen = GBU_FAKTOR_CODES.filter(c => c !== 'sonstige' && !abgedeckt.has(c));
  const hinweise = [GBU_ERSATZ_HINWEIS];
  if (offen.length) hinweise.push(`Ohne KI nicht betrachtet: ${offen.map(faktorText).join(', ')} — bitte selbst durchgehen.`);
  if (gekappt) hinweise.push(`Gekürzt auf ${GBU_KI_MAX_ERSATZ} Vorschläge — weitere Stoffe und Arbeitsmittel bitte selbst beurteilen.`);

  return { gefaehrdungen, kopf: {}, hinweise, abgeschnitten: false };
};

// ═══════════════════════════════════════════════════════════════════════════
// 7) AUFRUFE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * K1: Vorschlag fuer einen Arbeitsbereich. Wirft nie. Bei Ausfall
 * `status:'fehler'` mit Ersatz; Dubletten zu `kontext.vorhandene` sind in
 * beiden Faellen schon ausgeblendet.
 */
export const fordereGbuVorschlagAn = async (kontext: GbuKiKontext): Promise<GbuKiErgebnis> => {
  const ersatz = () => ohneDubletten(ersatzVorschlag(kontext), kontext.vorhandene);
  const antwort = await gbuVorschlag({ kontext: kontext.anfrage, codes: kontext.codes });
  if (antwort.ok === false) return { status: 'fehler', art: 'netzwerk', text: antwort.fehler, ersatz: ersatz() };
  let vorschlag: GbuKiVorschlag | null = null;
  try {
    vorschlag = leseGbuVorschlag(antwort.roh, kontext);
  } catch (e) {
    // Ein Fehler im Leser ist ein Programmfehler — er darf trotzdem nicht
    // als „keine Gefaehrdung" oder als weisse Ansicht enden.
    console.error('gbuVorschlag: Antwort nicht lesbar', e);
  }
  if (!vorschlag) return { status: 'fehler', art: 'antwort', text: GBU_KI_KEINE_ANTWORT, ersatz: ersatz() };
  return { status: 'ok', vorschlag: ohneDubletten(vorschlag, kontext.vorhandene) };
};

/**
 * K2: Vollstaendigkeit pruefen. Wirft nie. Die festen Regeln
 * (gbuLogic.pruefeGbu) laufen unabhaengig davon immer; diese Pruefung
 * ergaenzt sie. Abgeschnitten → ein eigener Hinweis am Ende der Liste.
 */
export const pruefeGbuMitKi = async (voll: GbuVoll, kataster: Kataster, branche?: string | null): Promise<GbuKiPruefErgebnis> => {
  const kontext = baueGbuPruefKontext(voll, kataster, branche);
  const antwort = await gbuPruefen({ kontext: kontext.anfrage, codes: kontext.codes });
  if (antwort.ok === false) return { status: 'fehler', art: 'netzwerk', text: antwort.fehler };
  let gelesen: ReturnType<typeof leseGbuPruefung> = null;
  try {
    gelesen = leseGbuPruefung(antwort.roh, kontext);
  } catch (e) {
    console.error('gbuPruefen: Antwort nicht lesbar', e);
  }
  if (!gelesen) return { status: 'fehler', art: 'antwort', text: GBU_KI_KEINE_ANTWORT };
  const hinweise = gelesen.abgeschnitten
    ? [...gelesen.hinweise, { art: 'luecke' as const, text: GBU_KI_PRUEFUNG_ABGESCHNITTEN, faktor: null, vorschlag: null }]
    : gelesen.hinweise;
  return { status: 'ok', hinweise };
};
